import type Token from 'markdown-it/lib/token';
import type StateCore from 'markdown-it/lib/rules_core/state_core';
import type {MarkdownItPluginCb, MarkdownItPluginOpts} from '@diplodoc/transform/lib/typings';

import {dirname, join} from 'node:path';
import {bold} from 'chalk';
import {extractFrontMatter} from '@diplodoc/liquid';

import {filterTokens, normalizePath} from '~/core/utils';

export interface IncludeChainEntry {
    file: string;
    line: number;
}

/**
 * Strips YAML frontmatter from include file content if present.
 * Include files written by md2md may have frontmatter (e.g. csp, metadata);
 * when used as include body they must not be rendered as content.
 */
export function contentWithoutFrontmatter(raw: string): string {
    const [, content] = extractFrontMatter(raw, {json: true});
    return content;
}

/**
 * Tags tokens (and their children) with include source metadata.
 * Tokens already tagged by a deeper nested include are not overwritten.
 */
function tagTokensWithSource(
    tokens: Token[],
    sourceFile: string,
    includeChain: IncludeChainEntry[],
) {
    for (const token of tokens) {
        if (!token.meta?.sourceFile) {
            token.meta = token.meta || {};
            token.meta.sourceFile = sourceFile;
            token.meta.includeChain = includeChain;
        }

        if (token.children) {
            tagTokensWithSource(token.children, sourceFile, includeChain);
        }
    }
}

function stripTitleTokens(tokens: Token[]) {
    const [open, _, close] = tokens;

    if (open?.type === 'heading_open' && close?.type === 'heading_close') {
        return tokens.slice(3);
    }

    return tokens;
}

type Options = MarkdownItPluginOpts & {
    path: NormalizedPath;
    files: Record<NormalizedPath, string>;
};

/**
 * Merges resource arrays (script, style) collected during nested
 * include parsing back into the parent env.  This is necessary because
 * `{...env}` spread breaks the `meta` getter/setter proxy — extensions
 * that run inside nested `md.parse` create a new `meta` object on the
 * copy, and their additions are lost without explicit propagation.
 */
export function mergeNestedMeta(
    parentEnv: Record<string, unknown>,
    nestedMeta: Record<string, unknown> | undefined,
) {
    if (!nestedMeta) {
        return;
    }

    const parent = (parentEnv.meta || {}) as Record<string, unknown>;
    parentEnv.meta = parent;

    for (const key of ['script', 'style']) {
        const items = nestedMeta[key];
        if (!Array.isArray(items) || items.length === 0) {
            continue;
        }
        const target: string[] = (parent[key] as string[]) || [];
        parent[key] = target;
        for (const item of items) {
            if (!target.includes(item)) {
                target.push(item);
            }
        }
    }
}

type CacheEntry = {tokens: Token[]; meta?: Record<string, unknown>};

export default ((md, options) => {
    const {path: optPath, log} = options;

    const cache: Hash<CacheEntry> = {};

    const plugin = (state: StateCore) => {
        const {env} = state;
        const path = env.path || optPath;

        env.includes = env.includes || [];

        const isCircularInclude = env.includes.includes(path);

        if (isCircularInclude) {
            log.error(`Circular includes: ${bold(env.includes.concat(path).join(' ▶ '))}`);
            return;
        }

        env.includes.push(path);
        unfoldIncludes(path, state, options, cache);
        env.includes.pop();
    };

    try {
        md.core.ruler.before('curly_attributes', 'includes', plugin);
    } catch {
        md.core.ruler.push('includes', plugin);
    }
}) as MarkdownItPluginCb<Options>;

function unfoldIncludes(
    path: NormalizedPath,
    state: StateCore,
    options: Options,
    cache: Hash<CacheEntry>,
) {
    const {log, files} = options;
    const {tokens, md, env} = state;

    // @ts-ignore
    filterTokens(tokens, 'include', (token, {index}) => {
        try {
            const includeLine = token.map ? token.map[0] + 1 : undefined;
            const includePath = token.attrGet('path') as string;
            const keyword = token.attrGet('keyword');

            if (includePath.startsWith('#')) {
                log.warn(
                    [
                        `YFM014 ${path}: Anchor "${includePath}" cannot be used as file path`,
                        ``,
                        `Expected syntax:`,
                        `  {% include [text](path/to/file.md) %}`,
                        ``,
                        `Include will be skipped.`,
                    ].join('\n'),
                );

                tokens.splice(index, 1);

                return {skip: 0};
            }

            const [pathname, hash] = includePath.split('#');
            const includeFullPath = normalizePath(join(dirname(path), pathname));
            const includeContent = files[includeFullPath];

            if (typeof includeContent !== 'string') {
                const includeLocation = includeLine ? `${path}:${includeLine}` : path;
                log.error(
                    `Include skipped in (${bold(includeLocation)}). Include source for ${bold(includeFullPath)} not found`,
                );

                return;
            }

            const bodyContent = contentWithoutFrontmatter(includeContent);

            const parentChain: IncludeChainEntry[] = env.includeChain || [];
            const currentChain: IncludeChainEntry[] = includeLine
                ? [...parentChain, {file: path, line: includeLine}]
                : [...parentChain];

            const cached = cache[includeFullPath];
            let fileTokens: Token[];
            let nestedMeta: Record<string, unknown> | undefined;

            if (cached) {
                fileTokens = cached.tokens;
                nestedMeta = cached.meta;
            } else {
                const nestedEnv = {
                    ...env,
                    path: includeFullPath,
                    includeChain: currentChain,
                };
                fileTokens = md.parse(bodyContent, nestedEnv);
                nestedMeta = nestedEnv.meta as Record<string, unknown> | undefined;
                cache[includeFullPath] = {tokens: fileTokens, meta: nestedMeta};
            }

            mergeNestedMeta(env, nestedMeta);

            let includedTokens: Token[];
            if (hash) {
                includedTokens = cutTokens(fileTokens, hash);

                if (includedTokens.length === 0) {
                    log.warn(
                        [
                            `YFM015 ${path}: Anchor "#${hash}" not found in ${includeFullPath}`,
                            ``,
                            `Include will be skipped.`,
                        ].join('\n'),
                    );
                }
            } else {
                includedTokens = fileTokens;
            }

            if (keyword === 'notitle') {
                includedTokens = stripTitleTokens(includedTokens);
            }

            // Always deep-copy the tokens before they enter the stream.  The
            // cached token tree is the canonical parse result and MUST stay
            // pristine: once tokens are spliced into the parent stream they are
            // mutated in place by later core rules (`curly_attributes`) and by
            // `tagTokensWithSource` below.  Previously we only copied on a cache
            // *hit*, so the first (cache-miss) consumer leaked the original
            // tokens into the stream and corrupted the cache for every
            // subsequent include of the same file.  Under `-j2` the order in
            // which files seed/mutate the shared cache is non-deterministic,
            // which produced flaky output (e.g. a `#hash` section randomly
            // losing or keeping a sibling heading).  Copying unconditionally
            // keeps the cache canonical and the output deterministic.
            includedTokens = includedTokens.map((token) => copyToken(state, token));

            tagTokensWithSource(includedTokens, includeFullPath, currentChain);

            tokens.splice(index, 1, ...includedTokens);

            return {skip: includedTokens.length};
        } catch (e) {
            // @ts-ignore for some reason typescript fails here
            const errPath = e.path;

            log.error(`Include skipped. Skip reason: ${e} in ${errPath}`);
        }
    });
}

function copyToken(state: StateCore, token: Token) {
    const result = new state.Token(token.type, token.tag, token.nesting);

    Object.assign(result, token);

    result.attrs = token.attrs?.map(([key, value]) => [key, value]) || null;
    result.map = (token.map?.slice() as [number, number]) || null;
    result.children = token.children?.map((token) => copyToken(state, token)) || null;

    return result;
}

function cutTokens(tokens: Token[], id: string) {
    const start = tokens.findIndex((token) => {
        return (
            (token.type === 'paragraph_open' || token.type === 'heading_open') &&
            token.attrGet('id') === id
        );
    });

    if (start === -1) {
        return [];
    }

    const startToken = tokens[start];

    switch (startToken.type) {
        case 'paragraph_open':
            return cutParagraph(tokens, start);
        case 'heading_open':
            return cutHeading(tokens, start);
        default:
            return [];
    }
}

function cutParagraph(tokens: Token[], start: number) {
    const end = tokens.findIndex((token, index) => {
        return token.type === 'paragraph_close' && index > start;
    });

    if (end === -1) {
        return [];
    }

    return tokens.slice(start, end + 1);
}

function cutHeading(tokens: Token[], start: number) {
    const level = Number(tokens[start].tag.slice(1));

    for (let index = start + 1; index < tokens.length; index++) {
        const token = tokens[index];

        // Section ends only at the next heading of the SAME level — this
        // matches `findBlockTokens` in @diplodoc/transform (the viewer's
        // source of truth) and `processHeadingForSection` in merge-includes.
        // Using `>=` here was a divergence: a nested include expanded inside
        // the section can introduce a shallower heading (e.g. an `### {#x}`
        // from an included file inside an `#### {#section}` block).  With
        // `>=` that shallower heading wrongly terminated the section, so a
        // `notitle` include collapsed to nothing.  Surfaced on alice
        // uz/smart-home/.../unruly.md (#quick-notifications → telegram).
        if (token.type === 'heading_open' && level === Number(token.tag.slice(1))) {
            return tokens.slice(start, index);
        }
    }

    return tokens.slice(start);
}
